import React, { useState, useRef, useEffect } from 'react';
import { 
    Box, 
    TextField, 
    InputAdornment, 
    List, 
    ListItem, 
    ListItemText, 
    Paper, 
    Popper, 
    ClickAwayListener,
    CircularProgress,
    Snackbar,
    Alert,
    ListItemButton,
    IconButton,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { searchEvents, getTypeaheadSuggestions, SearchEventsResponse, EventResponse, TypeaheadSuggestion } from '../services/eventService';
import SearchResults from './SearchResults';
import SearchDropdown from './SearchDropdown';
import ComingSoonTag from './ComingSoonTag';
import CloseIcon from '@mui/icons-material/Close';

interface SearchBarProps {
    onSearchResults?: (results: SearchEventsResponse) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearchResults }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState<TypeaheadSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<SearchEventsResponse | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Handle Escape key press
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSuggestions([]);
                setSearchResults(null);
                setAnchorEl(null);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        
        if (value.length >= 2) {
            fetchSuggestions(value);
        } else {
            setSuggestions([]);
        }
    };

    const fetchSuggestions = async (query: string) => {
        try {
            const response = await getTypeaheadSuggestions(query);
            if (Array.isArray(response)) {
                setSuggestions(response);
                setAnchorEl(inputRef.current);
            }
        } catch (err) {
            console.error('Error fetching suggestions:', err);
            setSuggestions([]);
        }
    };

    const handleSearchSubmit = async (event?: React.FormEvent) => {
        if (event) {
            event.preventDefault();
        }

        // Search functionality is disabled - show coming soon message
        setError('Search functionality is coming soon!');
        return;

        // Original search logic (commented out)
        /*
        if (!searchQuery.trim()) {
            setError('Please enter a search term');
            return;
        }

        setLoading(true);
        setError(null);
        setSuggestions([]);
        setAnchorEl(null);

        try {
            console.log('Searching for:', searchQuery);
            const response = await searchEvents({ query: searchQuery });
            console.log('Raw API response:', response);

            if (!response || typeof response !== 'object') {
                throw new Error('Invalid response format');
            }

            if (!Array.isArray(response.events)) {
                console.error('Events is not an array:', response.events);
                throw new Error('Invalid events data format');
            }

            setSearchResults(response);
            if (onSearchResults) {
                onSearchResults(response);
            }
        } catch (err) {
            console.error('Search error:', err);
            setError(err instanceof Error ? err.message : 'An error occurred during search');
            setSearchResults(null);
        } finally {
            setLoading(false);
        }
        */
    };

    const handleSuggestionClick = (suggestion: TypeaheadSuggestion) => {
        // Suggestions are disabled - search functionality is coming soon
        setError('Search functionality is coming soon!');
        return;
        
        // Original suggestion logic (commented out)
        /*
        setSearchQuery(suggestion.title);
        setSuggestions([]);
        setAnchorEl(null);
        handleSearchSubmit();
        */
    };

    const handleClickAway = () => {
        setSuggestions([]);
        setAnchorEl(null);
    };

    const handleCloseError = () => {
        setError(null);
    };

    const handleCloseSearchResults = () => {
        setSearchResults(null);
    };

    return (
        <Box sx={{ 
            width: '100%', 
            maxWidth: 800, 
            mx: 'auto',
            py: isMobile ? 2 : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
        }}>
            <Paper elevation={1} 
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    position: 'relative', 
                    borderRadius: 8 
                }}
            >
                <form onSubmit={handleSearchSubmit}>
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Search for events..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            inputRef={inputRef}
                            disabled={true}
                            InputProps={{
                                sx: { 
                                    borderRadius: 8,
                                    opacity: 0.6,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ opacity: 0.5 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: loading && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <ComingSoonTag 
                            variant="chip"
                            size="small"
                            position="absolute"
                            absolutePosition={{ top: 8, right: 8 }}
                        />
                    </Box>
                </form>

                <Box sx={{ position: 'relative', width: '100%' }}>
                    {/* Suggestions disabled - search functionality is coming soon */}
                    {false && (
                        <Popper
                            open={suggestions.length > 0}
                            anchorEl={anchorEl}
                            placement="bottom-start"
                            style={{ 
                                width: isMobile ? '90%' : '30%',
                                zIndex: 1300,
                                marginTop: '8px'
                            }}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 4],
                                    },
                                },
                                {
                                    name: 'preventOverflow',
                                    options: {
                                        boundary: 'clippingParents',
                                        padding: 16,
                                    },
                                },
                                {
                                    name: 'flip',
                                    options: {
                                        fallbackPlacements: ['bottom-start'],
                                    },
                                },
                            ]}
                        >
                            <ClickAwayListener onClickAway={handleClickAway}>
                                <div>
                                    <SearchDropdown>
                                        <List>
                                            {suggestions.map((suggestion, index) => (
                                                <ListItem key={`${suggestion.id}-${index}`} disablePadding>
                                                    <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                                                        <ListItemText primary={suggestion.title} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </SearchDropdown>
                                </div>
                            </ClickAwayListener>
                        </Popper>
                    )}

                    {/* Search results disabled - search functionality is coming soon */}
                    {false && searchResults && (
                        <ClickAwayListener onClickAway={handleCloseSearchResults}>
                            <div>
                                <SearchDropdown 
                                    title={`Search Results (${searchResults?.total || 0})`}
                                    action={
                                        <IconButton 
                                            size="small" 
                                            onClick={handleCloseSearchResults}
                                            sx={{ 
                                                position: 'absolute',
                                                right: 8,
                                                top: 8
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    {searchResults && (
                                        <SearchResults 
                                            results={searchResults!} 
                                            onEventClick={(event) => {
                                                // Handle event click (e.g., navigate to event details)
                                                console.log('Event clicked:', event);
                                            }}
                                        />
                                    )}
                                </SearchDropdown>
                            </div>
                        </ClickAwayListener>
                    )}
                </Box>
            </Paper>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SearchBar;
